import { toast } from "sonner";
import { getStoredToken } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Force the browser to save a protected PDF even when the app is embedded
 * in an iframe (Emergent preview pane) where programmatic a.click() downloads
 * can be blocked by the sandbox policy.
 *
 * Strategy:
 *   1. fetch() the PDF with credentials so we verify auth and surface errors.
 *   2. Build an object URL and trigger a real anchor click with target="_top".
 *      target="_top" escapes any enclosing iframe, so the top-level window
 *      receives the blob URL and shows / downloads it.
 *   3. As a hard fallback, open the direct API URL in a new top-level tab.
 */
async function downloadFile(apiPath, filename, accept = "application/pdf") {
  const url = `${BACKEND}${apiPath}`;

  const headers = { Accept: accept };
  const token = getStoredToken();
  if (token) headers.Authorization = `Bearer ${token}`; // respaldo si el navegador bloquea cookies
  const res = await fetch(url, {
    credentials: "include",
    headers,
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.clone().json();
      if (body?.detail) detail = body.detail;
    } catch (parseErr) {
      // Response body wasn't JSON; keep the generic HTTP status detail.
      // No need to surface this to the user.
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  if (blob.size === 0) throw new Error("Archivo vacío");

  if (window.navigator?.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, filename);
    return;
  }

  const objUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  a.target = "_top"; // escape iframe sandbox
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();

  setTimeout(() => {
    a.remove();
    window.URL.revokeObjectURL(objUrl);
  }, 2000);
}

async function safeExport(apiPath, filename, successMsg, accept = "application/pdf", loadingMsg = "Generando PDF…") {
  const loadingId = toast.loading(loadingMsg);
  try {
    await downloadFile(apiPath, filename, accept);
    toast.success(successMsg, { id: loadingId });
  } catch (e) {
    if (/no autenticado|401/i.test(e.message || "")) {
      toast.error("Tu sesión ha caducado. Cierra sesión y vuelve a entrar para descargar el PDF.", { id: loadingId });
      // eslint-disable-next-line no-console
      console.error("[export]", apiPath, e);
      return;
    }
    const fallbackUrl = `${BACKEND}${apiPath}`;
    toast.error(`No se pudo descargar (${e.message}). Abriendo en pestaña…`, { id: loadingId });
    window.open(fallbackUrl, "_blank", "noopener");
    // eslint-disable-next-line no-console
    console.error("[export]", apiPath, e);
  }
}

function qs({ date_from = "", date_to = "" } = {}) {
  const p = new URLSearchParams();
  if (date_from) p.set("date_from", date_from);
  if (date_to) p.set("date_to", date_to);
  const s = p.toString();
  return s ? `?${s}` : "";
}

export function exportTeamPdf(team, opts = {}) {
  return safeExport(
    `/api/export/team/${team.id}/pdf${qs(opts)}`,
    `equipo_${(team.name || "equipo").replace(/\s+/g, "_")}.pdf`,
    "PDF descargado",
  );
}

export function downloadPhysioPdf(apiPath, filename) {
  return safeExport(`/api${apiPath}`, filename, "PDF de fisioterapia descargado");
}

export function exportTeamDashboardPdf(team, season) {  return safeExport(
    `/api/export/team/${team.id}/dashboard-pdf?season=${season}`,
    `dashboard_${(team.name || "equipo").replace(/\s+/g, "_")}_${season}.pdf`,
    "PDF del dashboard descargado",
  );
}

export function exportAllPlayersXlsx() {
  return safeExport(
    `/api/export/all-players/xlsx`,
    `jugadores_club.xlsx`,
    "Excel descargado",
  );
}

export function exportPlayerPdf(player, opts = {}) {
  return safeExport(
    `/api/export/player/${player.id}/pdf${qs(opts)}`,
    `jugador_${(player.name || "jugador").replace(/\s+/g, "_")}.pdf`,
    "PDF descargado",
  );
}

export function exportPlayerQuarterlyPdf(player, opts = {}) {
  return safeExport(
    `/api/export/player/${player.id}/quarterly-pdf${qs(opts)}`,
    `informe_trimestral_${(player.name || "jugador").replace(/\s+/g, "_")}.pdf`,
    "Informe trimestral descargado",
  );
}

export function exportPlayerEvaluationPdf(playerId, playerName, season, period) {
  const safe = (playerName || "jugador").replace(/\s+/g, "_");
  return safeExport(
    `/api/export/player/${playerId}/evaluation-pdf?season=${encodeURIComponent(season)}&period=${encodeURIComponent(period)}`,
    `informe_${safe}_${season}_${period}.pdf`,
    "Informe descargado",
  );
}

export function exportRivalsDirectoryXlsx() {
  return safeExport(
    "/api/rivals-directory/export",
    "directorio_rivales.xlsx",
    "Directorio de rivales exportado",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "Generando Excel…",
  );
}

export function exportScoutingPlayerPdf(player) {
  const safe = (player.player_name || "jugador").replace(/\s+/g, "_");
  return safeExport(
    `/api/scouting-market/players/${player.id}/pdf`,
    `scouting_${safe}.pdf`,
    "Ficha de scouting descargada",
  );
}

export function exportPlayerCardPdf(player) {
  return safeExport(
    `/api/export/player/${player.id}/card-pdf`,
    `carnet_${(player.name || "jugador").replace(/\s+/g, "_")}.pdf`,
    "Carnet del jugador descargado",
  );
}

export function sharePlayerCardWhatsApp(player, team) {
  // Use String.fromCodePoint to guarantee UTF-8 emojis survive file encoding.
  const TROPHY = String.fromCodePoint(0x1F3C6); // 🏆
  const STAR = String.fromCodePoint(0x1F31F);   // 🌟
  const text = encodeURIComponent(
    `${TROPHY} *${player.name}* (#${player.dorsal || "-"})\n` +
    `${team?.name || ""}${team?.category ? " · " + team.category : ""}\n\n` +
    `Mira mi carnet del jugador ${STAR}`,
  );
  // We open WhatsApp with the prefilled text and also download the PDF.
  // The user can then attach the PDF in the chat.
  exportPlayerCardPdf(player);
  setTimeout(() => {
  // Use api.whatsapp.com/send (no wa.me redirect) to avoid mojibake injected
  // by WhatsApp's redirector when handling certain UTF-8 characters.
  window.open(`https://api.whatsapp.com/send?text=${text}`, "_blank", "noopener");
  }, 600);
}

export function exportMatchPdf(match) {
  return safeExport(
    `/api/export/match/${match.id}/pdf`,
    `convocatoria_${match.date}_${(match.rival || "partido").replace(/\s+/g, "_")}.pdf`,
    "Convocatoria descargada",
  );
}

const XLSX_MIME = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

export function exportTeamXlsx(team, opts = {}) {
  return safeExport(
    `/api/export/team/${team.id}/xlsx${qs(opts)}`,
    `equipo_${(team.name || "equipo").replace(/\s+/g, "_")}.xlsx`,
    "Excel descargado",
    XLSX_MIME,
    "Generando Excel…",
  );
}

export function exportPlayerXlsx(player, opts = {}) {
  return safeExport(
    `/api/export/player/${player.id}/xlsx${qs(opts)}`,
    `jugador_${(player.name || "jugador").replace(/\s+/g, "_")}.xlsx`,
    "Excel descargado",
    XLSX_MIME,
    "Generando Excel…",
  );
}

export function exportCalendarPdf({ year, month, team_id = "", kind = "", category = "", home_away = "" } = {}) {
  const p = new URLSearchParams({ year: String(year), month: String(month) });
  if (team_id) p.set("team_id", team_id);
  if (kind) p.set("kind", kind);
  if (category) p.set("category", category);
  if (home_away) p.set("home_away", home_away);
  const suffix = kind === "training" ? "_entrenamientos" : kind === "match" ? "_partidos" : "";
  const ha = home_away === "casa" ? "_casa" : home_away === "fuera" ? "_fuera" : "";
  return safeExport(
    `/api/export/calendar/pdf?${p}`,
    `calendario_${year}_${String(month).padStart(2, "0")}${suffix}${ha}.pdf`,
    "PDF descargado",
  );
}

export function exportCalendarAgendaPdf({ date_from, date_to, team_id = "", kind = "", category = "", home_away = "" } = {}) {
  const p = new URLSearchParams({ date_from, date_to });
  if (team_id) p.set("team_id", team_id);
  if (kind) p.set("kind", kind);
  if (category) p.set("category", category);
  if (home_away) p.set("home_away", home_away);
  const ha = home_away === "casa" ? "_casa" : home_away === "fuera" ? "_fuera" : "";
  return safeExport(
    `/api/export/calendar/agenda-pdf?${p}`,
    `agenda_${date_from}_${date_to}${ha}.pdf`,
    "PDF descargado",
  );
}

export function exportClubPdf(opts = {}) {
  return safeExport(
    `/api/export/club/pdf${qs(opts)}`,
    "club_resumen.pdf",
    "PDF del club descargado",
  );
}

export function exportClubXlsx(opts = {}) {
  return safeExport(
    `/api/export/club/xlsx${qs(opts)}`,
    "club_resumen.xlsx",
    "Excel del club descargado",
    XLSX_MIME,
    "Generando Excel del club…",
  );
}

export function exportClubRankingsPdf(opts = {}) {
  return safeExport(
    `/api/export/club/rankings_pdf${qs(opts)}`,
    "club_ranking.pdf",
    "Ranking del club descargado",
  );
}

export function exportTrainingPdf(training) {
  return safeExport(
    `/api/export/training/${training.id}/pdf`,
    `sesion_${training.date}_${(training.id || "").slice(0, 8)}.pdf`,
    "Sesión descargada",
  );
}

export function exportRivalTeamScoutingPdf(rivalTeam) {
  const params = new URLSearchParams({ rival_team: rivalTeam }).toString();
  const safe = (rivalTeam || "rival").replace(/\s+/g, "_");
  return safeExport(
    `/api/export/scouting/rival_team_pdf?${params}`,
    `scouting_${safe}.pdf`,
    "Dossier de rival descargado",
  );
}

export function exportPlayerDashboardPdf(playerId, playerName, phase) {
  const safe = (playerName || "jugador").replace(/\s+/g, "_");
  return safeExport(
    `/api/players/${playerId}/dashboard/pdf${phase ? `?phase=${phase}` : ""}`,
    `dashboard_${safe}.pdf`,
    "PDF del dashboard descargado",
  );
}

export function exportScoutingSelectionPdf(ids) {
  const params = new URLSearchParams({ ids: ids.join(",") }).toString();
  return safeExport(
    `/api/export/scouting/selection_pdf?${params}`,
    "scouting_seleccion.pdf",
    "PDF de la selección descargado",
  );
}

export function exportRivalComparisonPdf(ids) {
  const params = new URLSearchParams({ ids: ids.join(",") }).toString();
  return safeExport(
    `/api/export/scouting/compare_pdf?${params}`,
    "comparativa_rivales.pdf",
    "Comparativa de rivales descargada",
  );
}

export function exportScoutingMarketComparePdf(ids) {
  const params = new URLSearchParams({ ids: ids.join(",") }).toString();
  return safeExport(
    `/api/export/scouting-market/compare_pdf?${params}`,
    "comparativa_scouting.pdf",
    "Comparativa descargada",
  );
}

export function exportRivalDatasetComparePdf(ids) {
  const params = new URLSearchParams({ ids: ids.join(",") }).toString();
  return safeExport(
    `/api/export/rival-dataset/compare_pdf?${params}`,
    "comparativa_rivales.pdf",
    "Comparativa de rivales descargada",
  );
}

export function exportScoutingXlsx(params = {}) {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v) usp.append(k, v); });
  const qsStr = usp.toString();
  return safeExport(
    `/api/export/scouting/xlsx${qsStr ? `?${qsStr}` : ""}`,
    "scouting_base.xlsx",
    "Excel de scouting descargado",
    XLSX_MIME,
    "Generando Excel…",
  );
}

export function exportRivalPlayerPdf(jugadorId, playerName) {
  const safe = (playerName || "jugador").replace(/\s+/g, "_");
  return safeExport(
    `/api/rival-dataset/players/${jugadorId}/pdf`,
    `informe_rffm_${safe}.pdf`,
    "Informe RFFM del jugador descargado",
  );
}

export function exportRivalDossierPdf(rivalName, equipoEf) {
  const params = new URLSearchParams({ name: rivalName });
  if (equipoEf) params.set("equipo_ef", equipoEf);
  const safe = (rivalName || "rival").replace(/\s+/g, "_");
  return safeExport(
    `/api/rival-dataset/rival-dashboard/pdf?${params.toString()}`,
    `dossier_${safe}.pdf`,
    "Dossier de rival descargado",
  );
}

export function exportRivalCv(rivalId, filename) {
  return safeExport(
    `/api/rival-players/${rivalId}/cv`,
    filename || "curriculum",
    "CV descargado",
    "*/*",
    "Descargando CV…",
  );
}
